import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
	Form,
	Select,
	Row,
	Col,
	Space,
	Modal,
	Card,
	Divider,
	Typography,
	InputNumber,
	Switch,
	Tooltip,
} from "antd";
import {
	InnerForm,
	ProductData,
	PublishSettings,
	Container,
	CardContainer,
	InnerBlocks,
} from "./styles";
import { CloseCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { create_laboratory_suggestion } from "../../../../../api/endpoints/laboratorySuggestions";
import { useAuth } from "../../../../../contexts/authContext";
import {
	openNotificationWithIcon,
	TYPE,
} from "../../../../../utils/notificationToast";
import Main from "../../../../layout/main/Main";
import { COLORS, Typo } from "../../../../layout/theme";
import SignInTemplate from "../../../../layout/sign-in-template/SignInTemplate";
import WeightMixer from "./components/weight-mixer/WeightMixes";
import {
	get_clients_name_list,
	get_clients_name_list_filtered_suggested,
} from "../../../../../api/endpoints/clients";

const { Option } = Select;
const GUTTER = 10;

export default function CreateLaboratorySuggestion({ clientId }) {
	const [form] = Form.useForm();
	const { userdata } = useAuth();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const uri = searchParams.get("uri");

	const [submitting, setSubmitting] = useState(false);
	const [initialValues, setInitialValues] = useState(null);
	const [clients, setClients] = useState(null);
	const [selectedClient, setSelectedClient] = useState(clientId);
	const [show, setShow] = useState(null);
	const [showProductsModal, setShowProductsModal] = useState(false);
	const [curatedProducts, setCuratedProducts] = useState([]);
	const [weights, setWeights] = useState({
		weightOOS: 0.4,
		weightRecency: 0.25,
		weightFrequency: 0.2,
		weightLabCuration: 0.15,
		weightRecovery: 0.0,
	});

	useEffect(() => {
		const fetch_market_clients = async () => {
			const res = await get_clients_name_list_filtered_suggested();
			setClients(
				res
					?.filter((c) => c.isApproved)
					?.map((r) => ({
						value: r.id,
						label: r.name,
					}))
			);
		};

		const fetch_defaul_values = () => {
			form.setFieldsValue({
				lookbackDaysRecency: 90,
				lookbackMonthsFrequency: 6,
				lookbackDaysOOS: 60,
				lookbackMonthsRecovery: 12,
				recoveryDaysTrigger: 45,
				recoveryDaysFull: 120,
				recoveryMinMonths: 2,
				maxSuggestionsPerPOS: 30,
				minOOSFirst: 5,
				forceLabCurated: true,
				excludePausedProducts: true,
				excludeDraftProducts: true,
				state: "ACTIVE",
			});
		};

		fetch_market_clients();
		fetch_defaul_values();
	}, []);

	const validateWeightsSum = () => {
		const sum =
			weights.weightOOS +
			weights.weightRecency +
			weights.weightFrequency +
			weights.weightLabCuration +
			weights.weightRecovery;
		return Math.abs(sum - 1) < 0.001;
	};

	const onFinish = async (values) => {
		if (!validateWeightsSum()) {
			openNotificationWithIcon(
				TYPE.ERROR,
				"Pesos inválidos",
				"La suma de los pesos debe ser exactamente 1.0"
			);
			return;
		}

		try {
			setSubmitting(true);

			const labSuggestionPayload = {
				laboratoryId: selectedClient,
				isEnabled: values.state === "ACTIVE",

				weightOOS: weights.weightOOS,
				weightRecency: weights.weightRecency,
				weightFrequency: weights.weightFrequency,
				weightLabCuration: weights.weightLabCuration,
				weightRecovery: weights.weightRecovery,

				lookbackDaysRecency: Number(values.lookbackDaysRecency),
				lookbackMonthsFrequency: Number(values.lookbackMonthsFrequency),
				lookbackMonthsRecovery: Number(values.lookbackMonthsRecovery),
				lookbackDaysOOS: Number(values.lookbackDaysOOS),

				recoveryDaysTrigger: Number(values.recoveryDaysTrigger),
				recoveryDaysFull: Number(values.recoveryDaysFull),
				recoveryMinMonths: Number(values.recoveryMinMonths),

				maxSuggestionsPerPOS: Number(values.maxSuggestionsPerPOS),
				minOOSFirst: Number(values.minOOSFirst),
				forceLabCurated: !!values.forceLabCurated,

				excludePausedProducts: !!values.excludePausedProducts,
				excludeDraftProducts: !!values.excludeDraftProducts,
			};

			const result = await create_laboratory_suggestion(labSuggestionPayload);

			openNotificationWithIcon(
				TYPE.SUCCESS,
				"Configuracion de sugeridos creada con éxito",
				"La configuracion de sugeridos se creo con éxito"
			);
			navigate(`/admin/laboratorySuggestions/${result}?uri=${uri}`);
		} catch (error) {
			openNotificationWithIcon(
				TYPE.ERROR,
				"Error al crear los datos",
				error?.response?.data?.title ||
					"Un error ocurrio al intentar crear los datos, contacte con soporte a soporte@checkpos.com."
			);
		} finally {
			setSubmitting(false);
		}
	};

	const handleBack = () => {
		Modal.confirm({
			title: "¿Seguro que quiere volver sin guardar?",
			icon: <CloseCircleOutlined style={{ color: "red" }} />,
			content:
				"Si regresa sin guardar se perderán los datos que hayan sido cargados",
			okText: "Sí",
			okType: "danger",
			cancelText: "No",
			onOk: () => {
				navigate("/admin/laboratorySuggestions?uri=" + uri);
			},
		});
	};

	const handleClientSelect = (value, object) => {
		if (userdata.isOneMarket) setSelectedClient(object.value);
	};

	return (
		<Main>
			<Main.Header>
				<Main.Breadcrumb>
					<Main.BreadcrumbItem>
						{uri ? (
							<Link to={"/" + uri}>Configuración General</Link>
						) : (
							<Link to="/">Home</Link>
						)}
					</Main.BreadcrumbItem>
					<Main.BreadcrumbSeparator />
					<Main.BreadcrumbItem>
						<Link to={"/admin/laboratorySuggestions?uri=" + uri}>
							Configuracion de sugeridos
						</Link>
					</Main.BreadcrumbItem>
					<Main.BreadcrumbSeparator />
					<Main.ActiveBreadcrumbItem>
						Crear configuracion
					</Main.ActiveBreadcrumbItem>
				</Main.Breadcrumb>
			</Main.Header>

			<Main.Body>
				<Form
					form={form}
					name="lab-suggestions"
					onFinish={onFinish}
					autoComplete="off"
					requiredMark={false}
					layout="vertical"
				>
					<InnerBlocks>
						<InnerForm>
							<ProductData>
								<CardContainer>
									<Row gutter={GUTTER}>
										<Col span={24}>
											<Form.Item
												label={
													<label
														style={{ color: COLORS.Primary, fontWeight: "600" }}
													>
														Laboratorio
													</label>
												}
												name="laboratoryId"
												rules={[
													{
														required: true,
														message: "Este campo es obligatorio",
													},
												]}
											>
												<Select
													showSearch
													placeholder="Elegi un laboratorio"
													optionFilterProp="children"
													onChange={handleClientSelect}
													value={
														selectedClient !== "null" ? selectedClient : "..."
													}
													filterOption={(input, option) =>
														(option?.label ?? "")
															.toLowerCase()
															.includes(input.toLowerCase())
													}
													options={clients}
													disabled={!userdata.isOneMarket}
												/>
											</Form.Item>
										</Col>
									</Row>
								</CardContainer>

								{/* PESOS */}
								<CardContainer>
									<Card
										title={
											<Typo level={5}>
												Pesos señales de interes{" "}
												<Tooltip
													title={`El puntaje final es una suma ponderada de cada peso que genera un valor que mide importancia de cada producto. Más peso en una señal = más impacto en el ranking.`}
													placement="topLeft"
													arrowPointAtCenter
												>
													<InfoCircleOutlined
														style={{
															color: COLORS.Secondary,
															paddingRight: 10,
														}}
													/>
												</Tooltip>
											</Typo>
										}
									>
										<WeightMixer value={weights} onChange={setWeights} />
										<Divider />
										<Row gutter={12}>
											<Col span={24}>
												<Typography.Text
													type={validateWeightsSum() ? "secondary" : "danger"}
												>
													Suma actual:{" "}
													{(
														weights.weightOOS +
														weights.weightRecency +
														weights.weightFrequency +
														weights.weightLabCuration +
														weights.weightRecovery
													).toFixed(2)}
												</Typography.Text>
											</Col>
										</Row>
									</Card>
								</CardContainer>
							</ProductData>

							<PublishSettings>
								<Container>
									<Space
										direction="vertical"
										size="middle"
										style={{ display: "flex" }}
									>
										<Form.Item>
											<SignInTemplate.AntdButton
												type="Primary"
												color="white"
												bg="Primary"
												htmlType="submit"
												loading={submitting}
												block
											>
												Guardar Cambios
											</SignInTemplate.AntdButton>
										</Form.Item>
										<SignInTemplate.AntdButton
											type="Primary"
											onClick={handleBack}
											loading={submitting}
											block
										>
											<Typo type="danger" level={6}>
												Volver sin guardar
											</Typo>
										</SignInTemplate.AntdButton>
									</Space>
								</Container>
							</PublishSettings>
						</InnerForm>
						<CardContainer>
							<Card
								title={<Typo level={5}>Configuración de cálculo</Typo>}
								bodyStyle={{ paddingTop: 16 }}
							>
								<Row gutter={GUTTER}>
									{/* Columna 1: Ventanas */}
									<Col xs={24} md={12} lg={6}>
										<Card
											size="small"
											type="inner"
											title="Ventanas"
											bordered={true}
										>
											<Form.Item
												label={
													<Typo level={16} style={{ whiteSpace: "nowrap" }}>
														Ventana Faltantes (días)
													</Typo>
												}
												name="lookbackDaysOOS"
												rules={[{ required: true }]}
											>
												<InputNumber min={1} style={{ width: "100%" }} />
											</Form.Item>
											<Form.Item
												label={
													<Typo level={16} style={{ whiteSpace: "nowrap" }}>
														Ventana Recencia (días)
													</Typo>
												}
												name="lookbackDaysRecency"
												rules={[{ required: true }]}
											>
												<InputNumber min={1} style={{ width: "100%" }} />
											</Form.Item>
											<Form.Item
												label={
													<Typo level={16} style={{ whiteSpace: "nowrap" }}>
														Ventana Frecuencia (meses)
													</Typo>
												}
												name="lookbackMonthsFrequency"
												rules={[{ required: true }]}
											>
												<InputNumber min={1} style={{ width: "100%" }} />
											</Form.Item>
											<Form.Item
												label={
													<Typo level={16} style={{ whiteSpace: "nowrap" }}>
														Ventana Recupero (meses)
													</Typo>
												}
												name="lookbackMonthsRecovery"
												rules={[{ required: true }]}
											>
												<InputNumber min={1} style={{ width: "100%" }} />
											</Form.Item>
										</Card>
									</Col>

									{/* Columna 2: Recupero */}
									<Col xs={24} md={12} lg={6}>
										<Card
											size="small"
											type="inner"
											title="Recupero"
											bordered={true}
										>
											<Form.Item
												label={<Typo level={16}>Umbral inicio (días)</Typo>}
												name="recoveryDaysTrigger"
												rules={[{ required: true }]}
											>
												<InputNumber min={1} style={{ width: "100%" }} />
											</Form.Item>
											<Form.Item
												label={<Typo level={16}>Abandono pleno (días)</Typo>}
												name="recoveryDaysFull"
												rules={[{ required: true }]}
											>
												<InputNumber min={1} style={{ width: "100%" }} />
											</Form.Item>
											<Form.Item
												label={<Typo level={16}>Meses históricos mínimos</Typo>}
												name="recoveryMinMonths"
												rules={[{ required: true }]}
											>
												<InputNumber min={1} style={{ width: "100%" }} />
											</Form.Item>
										</Card>
									</Col>

									{/* Columna 3: Límites y priorización */}
									<Col xs={24} md={12} lg={6}>
										<Card
											size="small"
											type="inner"
											title="Límites y priorización"
											bordered={true}
										>
											<Form.Item
												label={<Typo level={16}>Máx. sugerencias por PDV</Typo>}
												name="maxSuggestionsPerPOS"
												rules={[{ required: true }]}
											>
												<InputNumber min={1} style={{ width: "100%" }} />
											</Form.Item>
											<Form.Item
												label={
													<Typo level={16}>Mín. faltantes priorizados</Typo>
												}
												name="minOOSFirst"
											>
												<InputNumber min={0} style={{ width: "100%" }} />
											</Form.Item>
											<Form.Item
												label={<Typo level={16}>Forzar destacados</Typo>}
												name="forceLabCurated"
												valuePropName="checked"
											>
												<Switch />
											</Form.Item>
										</Card>
									</Col>

									{/* Columna 4: Filtrado y estado */}
									<Col xs={24} md={12} lg={6}>
										<Card
											size="small"
											type="inner"
											title="Filtrado y estado"
											bordered={true}
										>
											<Form.Item
												label="Excluir productos pausados"
												name="excludePausedProducts"
												valuePropName="checked"
											>
												<Switch />
											</Form.Item>
											<Form.Item
												label="Excluir productos borrador"
												name="excludeDraftProducts"
												valuePropName="checked"
											>
												<Switch />
											</Form.Item>
											<Form.Item
												label="Estado"
												name="state"
												rules={[
													{
														required: true,
														message: "Este campo es obligatorio",
													},
												]}
											>
												<Select allowClear placeholder="Estado">
													<Option value="ACTIVE">Activo</Option>
													<Option value="DELETED">Eliminado</Option>
												</Select>
											</Form.Item>
										</Card>
									</Col>
								</Row>
							</Card>
						</CardContainer>
					</InnerBlocks>
				</Form>
			</Main.Body>
		</Main>
	);
}
